import React, { useState, useEffect } from 'react';
import { DataService } from '../services/dataService';
import { ExpandablePhaseBreakdown } from './ExpandablePhaseBreakdown';
import { ExpandableSplits } from './ExpandableSplits';
import { ExpandableElevation } from './ExpandableElevation';
import { ExpandableGPSRouteMap } from './ExpandableGPSRouteMap';
import { ExpandableExercises } from './ExpandableExercises';
import { ExpandableHIITBreakdown } from './ExpandableHIITBreakdown';
import { ExpandableHeartRateZones } from './ExpandableHeartRateZones';
import { ExpandablePowerCadence } from './ExpandablePowerCadence';
import { ExpandableSegments } from './ExpandableSegments';
import { HowItWentSection } from './HowItWentSection';
import { DataSourceBadges } from './badges/DataSourceBadges';
import { PushedToDeviceBadge } from './badges/PushedToDeviceBadge';
import { PoweredByAttribution } from './badges/PoweredByAttribution';
import { shouldShowPushStatus, shouldShowDataSources, getDataSources, getExternalSources } from '../utils/attributionHelpers';
import {
  hasPhases,
  hasSplits,
  hasGPSData,
  hasElevationData,
  hasHeartRateData,
  hasPowerOrCadenceData,
  hasSegments,
  hasExercises
} from '../utils/dataRichnessHelpers';

interface WorkoutHistoryDisplayProps {
  workoutId?: string;
  mobilityId?: string;
  maxSessions?: number;
}

export const WorkoutHistoryDisplay: React.FC<WorkoutHistoryDisplayProps> = ({ 
  workoutId, 
  mobilityId, 
  maxSessions = 3 
}) => {
  const [sessionHistory, setSessionHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSessionHistory = async () => {
      setIsLoading(true);
      try {
        let history: any[] = [];
        if (workoutId) {
          history = await DataService.getWorkoutSessionHistory(workoutId);
        } else if (mobilityId) {
          history = await DataService.getMobilitySessionHistory(mobilityId);
        }
        setSessionHistory(history || []);
      } catch (error) {
        console.error('Error loading session history:', error);
        setSessionHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSessionHistory();
  }, [workoutId, mobilityId]);

  if (isLoading) {
    return null; // Don't show loading state, just return null while loading
  }

  if (!sessionHistory || sessionHistory.length === 0) {
    return null;
  }

  const displaySessions = sessionHistory.slice(0, maxSessions);

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (date: string | Date): string => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return d.toLocaleDateString(undefined, { 
        month: 'short', 
        day: 'numeric',
        year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const renderCardioSession = (session: any) => {
    const data = session.sessionData;
    return (
      <div className="space-y-3">
        {/* Primary Metrics */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {data.distance ? `${(data.distance / 1000).toFixed(1)} km` : '--'}
            </div>
            <div className="text-xs text-gray-500">Distance</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {data.avgPace ? `${Math.floor(data.avgPace / 60)}:${(data.avgPace % 60).toString().padStart(2, '0')}` : '--'}
            </div>
            <div className="text-xs text-gray-500">Avg Pace</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {data.calories || '--'}
            </div>
            <div className="text-xs text-gray-500">Calories</div>
          </div>
        </div>

        {/* How It Went Section - Sprint 2 Task 4 */}
        {session.activityId && (
          <HowItWentSection activityId={session.activityId} />
        )}

        {/* Progressive Disclosure Components - Sprint 3 Task 1 */}
        {hasPhases(data) && (
          <ExpandablePhaseBreakdown phases={data.phases} />
        )}
        {hasSplits(data) && (
          <ExpandableSplits splits={data.splits} />
        )}
        {hasGPSData(data) && (
          <ExpandableGPSRouteMap session={data} />
        )}
        {hasElevationData(data) && (
          <ExpandableElevation data={{
            elevationGain: data.elevationGain,
            elevationLoss: data.elevationLoss,
            gpsData: data.gpsData
          }} />
        )}
        {hasHeartRateData(data) && (
          <ExpandableHeartRateZones data={{
            avgHeartRate: data.avgHeartRate,
            maxHeartRate: data.maxHeartRate,
            hrZones: data.hrZones,
            samples: data.heartRateSamples
          }} intensityLanguage={session?.workout_data?.intensity_language ?? null} />
        )}
        {hasPowerOrCadenceData(data) && (
          <ExpandablePowerCadence data={{
            avgPower: data.avgPower,
            maxPower: data.maxPower,
            normalizedPower: data.normalizedPower,
            avgCadence: data.avgCadence,
            maxCadence: data.maxCadence,
            powerZones: data.powerZones,
            samples: data.powerCadenceSamples
          }} />
        )}
        {hasSegments(data) && (
          <ExpandableSegments 
            segments={data.segments}
            stravaActivityId={data.stravaActivityId}
          />
        )}
      </div>
    );
  };

  const renderIntervalSession = (session: any) => {
    const data = session.sessionData;
    return (
      <div className="space-y-3">
        {/* Primary Metrics */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {data.distance ? `${(data.distance / 1000).toFixed(1)} km` : '--'}
            </div>
            <div className="text-xs text-gray-500">Distance</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {data.avgPace ? `${Math.floor(data.avgPace / 60)}:${(data.avgPace % 60).toString().padStart(2, '0')}` : '--'}
            </div>
            <div className="text-xs text-gray-500">Avg Pace</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {data.calories || '--'}
            </div>
            <div className="text-xs text-gray-500">Calories</div>
          </div>
        </div>

        {/* How It Went Section - Sprint 2 Task 4 */}
        {session.activityId && (
          <HowItWentSection activityId={session.activityId} />
        )}

        {/* Structured Workout - Progressive Disclosure - Sprint 3 Task 1 */}
        {hasPhases(data) && (
          <ExpandablePhaseBreakdown phases={data.phases} />
        )}
        {hasSplits(data) && (
          <ExpandableSplits splits={data.splits} />
        )}
        {hasGPSData(data) && (
          <ExpandableGPSRouteMap session={data} />
        )}
        {hasElevationData(data) && (
          <ExpandableElevation data={{
            elevationGain: data.elevationGain,
            elevationLoss: data.elevationLoss,
            gpsData: data.gpsData
          }} />
        )}
        {hasHeartRateData(data) && (
          <ExpandableHeartRateZones data={{
            avgHeartRate: data.avgHeartRate,
            maxHeartRate: data.maxHeartRate,
            hrZones: data.hrZones,
            samples: data.heartRateSamples
          }} intensityLanguage={session?.workout_data?.intensity_language ?? null} />
        )}
        {hasPowerOrCadenceData(data) && (
          <ExpandablePowerCadence data={{
            avgPower: data.avgPower,
            maxPower: data.maxPower,
            normalizedPower: data.normalizedPower,
            avgCadence: data.avgCadence,
            maxCadence: data.maxCadence,
            powerZones: data.powerZones,
            samples: data.powerCadenceSamples
          }} />
        )}
        {hasSegments(data) && (
          <ExpandableSegments 
            segments={data.segments}
            stravaActivityId={data.stravaActivityId}
          />
        )}
      </div>
    );
  };

  const renderStrengthMobilitySession = (session: any) => {
    const data = session.sessionData;
    const exerciseCount = data.exercises?.length || 0;
    const avgExerciseTime = exerciseCount > 0 ? Math.floor(data.totalDuration / exerciseCount) : 0;
    
    // Check if this is a HIIT workout
    const isHIIT = data.hiitType || data.circuits || data.emom || data.amrap;
    const hiitType = data.hiitType || (data.circuits ? 'circuit' : data.emom ? 'emom' : data.amrap ? 'amrap' : null);
    
    return (
      <div className="space-y-3">
        {/* Primary Metrics */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="text-center">
            <div className="font-semibold text-gray-900">{exerciseCount}</div>
            <div className="text-xs text-gray-500">Exercises</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {avgExerciseTime > 0 ? formatDuration(avgExerciseTime) : '--'}
            </div>
            <div className="text-xs text-gray-500">Avg Time</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {data.exercises?.filter((ex: any) => ex.completed).length || exerciseCount}
            </div>
            <div className="text-xs text-gray-500">Completed</div>
          </div>
        </div>

        {/* How It Went Section - Sprint 2 Task 4 */}
        {session.activityId && (
          <HowItWentSection activityId={session.activityId} />
        )}

        {/* Progressive Disclosure - Sprint 3 Task 1 */}
        {isHIIT && hiitType ? (
          <ExpandableHIITBreakdown
            hiitType={hiitType}
            circuits={data.circuits}
            emom={data.emom}
            amrap={data.amrap}
          />
        ) : (
          hasExercises(data) && (
            <ExpandableExercises exercises={data.exercises} />
          )
        )}
        {hasHeartRateData(data) && (
          <ExpandableHeartRateZones data={{
            avgHeartRate: data.avgHeartRate,
            maxHeartRate: data.maxHeartRate,
            hrZones: data.hrZones,
            samples: data.heartRateSamples
          }} intensityLanguage={session?.workout_data?.intensity_language ?? null} />
        )}
      </div>
    );
  };

  const getSessionLayout = (session: any): 'interval' | 'cardio' | 'strength' | 'workout' => {
    const workoutType = session.workout_type; // "running-tempo", "running-intervals", etc.
    const activityType = session.activityType; // "cardio", "strength", etc.
    const phases = session.sessionData?.phases || session.workout_data?.phases || [];
    
    // Priority 1: Check for HIIT/Interval keywords in workout_type
    if (workoutType?.includes('interval') || workoutType?.includes('tempo') || 
        workoutType?.includes('hiit') || workoutType?.includes('circuit')) {
      return 'interval';
    }
    
    // Priority 2: Check phase structure (>3 phases = structured workout)
    if (phases.length > 3) {
      return 'interval';
    }
    
    // Priority 3: Fall back to activity type
    if (activityType === 'cardio') return 'cardio';
    if (activityType === 'strength') return 'strength';
    
    return 'workout'; // Default
  };

  const getSessionTypeIcon = (sessionType: string) => {
    switch (sessionType) {
      case 'cardio':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'strength':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        );
      case 'mobility':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Session History
        </h4>
        {sessionHistory.length > maxSessions && (
          <span className="text-xs text-gray-500">
            +{sessionHistory.length - maxSessions} more
          </span>
        )}
      </div>

      <div className="space-y-3">
        {displaySessions.map((session) => {
          // Get the appropriate layout based on workout type and structure
          const layoutType = getSessionLayout(session);
          const sessionType = session.activityType || session.sessionData?.type || session.sessionType || 'workout';
          
          return (
            <div key={session.id} className="bg-white rounded border border-gray-200 p-3">
              {/* Session Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getSessionTypeIcon(sessionType)}
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(session.completedAt)}
                  </span>
                  {/* State-based badge display */}
                  {shouldShowPushStatus(session) ? (
                    <PushedToDeviceBadge 
                      platform={session.watchPlatform || 'garmin'}
                      deviceModel={session.deviceModel || session.sessionData?.deviceModel}
                      size="small"
                    />
                  ) : shouldShowDataSources(session) ? (
                    <DataSourceBadges 
                      sources={getDataSources(session.sessionData || session)}
                      size="small"
                    />
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    {formatDuration(session.sessionData?.totalDuration || 0)}
                  </span>
                  <div className="w-2 h-2 bg-green-500 rounded-full" title="Completed"></div>
                </div>
              </div>

              {/* Mission 22: Post-Workout Insights (if workout completed) */}
              {session.completed_at && (session.activity_id || session.id) && (
                <HowItWentSection 
                  activityId={session.activity_id || session.id} 
                  className="mb-4"
                />
              )}

              {/* Brand Attribution Footer (only post-completion with external sources) */}
              {shouldShowDataSources(session) && getExternalSources(session.sessionData || session).length > 0 && (
                <PoweredByAttribution 
                  sources={getExternalSources(session.sessionData || session)}
                  theme="light"
                  layout="auto"
                />
              )}

              {/* Session Data - Route to appropriate layout */}
              {(() => {
                switch (layoutType) {
                  case 'interval':
                    return renderIntervalSession(session);
                  case 'cardio':
                    return renderCardioSession(session);
                  case 'strength':
                    return renderStrengthMobilitySession(session);
                  default:
                    return renderStrengthMobilitySession(session);
                }
              })()}
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      {sessionHistory.length > 1 && (
        <div className="pt-2 border-t border-gray-200">
          <div className="flex justify-between text-xs text-gray-600">
            <span>{sessionHistory.length} total sessions</span>
            <span>
              Last: {formatDate(sessionHistory[0].completedAt)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
